import React, { useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';

/**
 * 延迟加载图片组件，提高页面加载性能
 * @param {string} src - 图片URL
 * @param {string} alt - 图片替代文本
 * @param {object} sx - MUI样式对象
 * @param {object} imgStyle - 直接应用于img标签的样式对象
 * @param {string} placeholder - 可选的占位图URL
 * @param {function} onLoad - 图片加载完成时的回调函数
 * @param {function} onError - 图片加载失败时的回调函数
 * @param {boolean} disableSkeleton - 是否禁用加载中的骨架屏
 * @param {object} props - 其他属性将被传递给img标签
 */
const LazyImage = ({
  src,
  alt,
  sx = {},
  imgStyle = {},
  placeholder = '',
  onLoad,
  onError,
  disableSkeleton = false,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState(placeholder || src);
  
  useEffect(() => {
    // 当传入的src变更时重置状态
    setIsLoaded(false);
    setError(false);
    setImageSrc(placeholder || src);
  }, [src, placeholder]);
  
  useEffect(() => {
    // 仅当组件挂载后且有src才加载图片
    if (!src) return;
    
    const img = new Image();
    img.src = src;
    
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
      if (onLoad) onLoad();
    };
    
    img.onerror = () => {
      setError(true);
      if (onError) onError();
    };
    
    // 组件卸载时清理
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, onLoad, onError]);
  
  const combinedStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease',
    opacity: isLoaded ? 1 : 0,
    ...imgStyle
  };
  
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx
      }}
    >
      {!isLoaded && !disableSkeleton && !error && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height="100%"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bgcolor: 'grey.200',
            zIndex: 1
          }}
        />
      )}
      
      <img
        src={imageSrc}
        alt={alt || '图片'}
        style={combinedStyle}
        loading="lazy"
        {...props}
      />
      
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            color: 'text.secondary',
            fontSize: '0.75rem',
            zIndex: 2
          }}
        >
          图片加载失败
        </Box>
      )}
    </Box>
  );
};

export default LazyImage;